export type ColorMode = 'light' | 'dark';

class ModeState {
    public current: ColorMode = $state('light');
    public isManual = false;
}

export class ModeService {
    /**
     * Der Schlüssel und die Body-Klassen müssen zum Inline-Skript in frontend/index.html passen.
     * Das Skript setzt den Modus schon vor dem ersten Paint, damit es kein Aufblitzen des
     * falschen Modus gibt - hier wird derselbe Stand danach nur noch weitergepflegt.
     */
    private static readonly StorageKey = 'LyColorMode';
    private static readonly SystemQuery = '(prefers-color-scheme: dark)';
    private static readonly BodyClasses: Record<ColorMode, string> = {
        light: 'LyLightMode',
        dark: 'LyDarkMode',
    };

    /** Muss als letztes Feld stehen: Der Aufbau greift auf die Konstanten darüber zu. */
    private static readonly State = ModeService.createState();

    public getMode(): ColorMode {
        return ModeService.State.current;
    }

    public isMode(mode: ColorMode): boolean {
        return ModeService.State.current === mode;
    }

    /** Sobald einmal von Hand gewählt wurde, hat dieser Vorrang vor dem Betriebssystem. */
    public setMode(mode: ColorMode): void {
        ModeService.State.isManual = true;
        ModeService.State.current = mode;
        this.applyMode(mode);

        try {
            window.localStorage.setItem(ModeService.StorageKey, mode);
        } catch {
            // Der Modus gilt dann nur für diese Sitzung.
        }
    }

    /** Einmalig beim Start aufrufen: übernimmt Systemwechsel, solange nichts von Hand gewählt wurde. */
    public init(): void {
        window.matchMedia(ModeService.SystemQuery).addEventListener('change', (event: MediaQueryListEvent): void => {
            if (ModeService.State.isManual) {
                return;
            }

            ModeService.State.current = event.matches ? 'dark' : 'light';
            this.applyMode(ModeService.State.current);
        });

        this.applyMode(ModeService.State.current);
    }

    private applyMode(mode: ColorMode): void {
        document.body.className = ModeService.BodyClasses[mode];
    }

    private static createState(): ModeState {
        const State = new ModeState();
        const Stored = ModeService.readStoredMode();

        State.current = Stored ?? ModeService.readSystemMode();
        State.isManual = Stored !== null;

        return State;
    }

    private static readStoredMode(): ColorMode | null {
        try {
            const Stored = window.localStorage.getItem(ModeService.StorageKey);
            return ModeService.isColorMode(Stored) ? Stored : null;
        } catch {
            // Kein Zugriff auf den Speicher? Dann eben ohne!
            return null;
        }
    }

    private static readSystemMode(): ColorMode {
        return window.matchMedia(ModeService.SystemQuery).matches ? 'dark' : 'light';
    }

    private static isColorMode(value: string | null): value is ColorMode {
        return value !== null && Object.hasOwn(ModeService.BodyClasses, value);
    }
}
